import {
    MAX_PRICE_AGE_FOR_APROXIMATED_USE_MS,
    MAX_PRICE_AGE_FOR_USE_MS,
    PRICEx1e6_DECIMALS,
    SYNC_SERVER_TIME_ALWAYS_MS,
    TOKEN_ADA_DECIMALS,
    TOKEN_ADA_SYMBOL,
    TOKEN_USD_DECIMALS,
    TOKEN_USD_SYMBOL,
} from '@/src/lib/SmartDB/Commons/Constants/constants';
import { formatAmountWithUnit } from '@/src/lib/SmartDB/Commons/formatters';
import { ADA_HISTORIC_PRICE_ENDPOINT, ADA_PRICE_ENDPOINT } from '@/src/utils/constants';
import { Action, Computed, Thunk, action, computed, thunk } from 'easy-peasy';
import {
    CS,
    Decimals,
    FundWithDetailsEntity,
    PriceApi,
    TN,
    TimeApi,
    TokenMetadataApi,
    TokenMetadataEntity,
    Token_With_Price_And_Date_And_Signature,
    fetchWrapper,
    hexToStr,
    isTokenADA,
    isToken_CS_And_TN_Valid,
    toJson,
} from '../../lib/MayzSmartDB';
import { AdaPriceApiResponse, AdaPriceHistoricApiResponse, DisplayCurrency, TokensToAdd as JobTokensToAdd, Token_For_Store, Token_For_Store_With_Validity } from './types';

//------------------------------------

export interface ITokensStoreModel {
    serverTime: number | undefined;
    setServerTime: Action<ITokensStoreModel, number | undefined>;

    serverTimeLastFetch: number | undefined;
    setServerTimeLastFetch: Action<ITokensStoreModel, number | undefined>;

    serverTimeDiffWithBrowser: number | undefined;
    setServerTimeDiffWithBrowser: Action<ITokensStoreModel, number | undefined>;

    isServerTimeLoaded: boolean;
    setIsServerTimeLoaded: Action<ITokensStoreModel, boolean>;

    getServerTime: Thunk<ITokensStoreModel, { refresh?: boolean } | undefined, any, any, Promise<number | undefined>>;

    jobTokensToAdd: JobTokensToAdd[];
    setJobTokensToAdd: Action<ITokensStoreModel, JobTokensToAdd[]>;

    jobTokensToAddFinished: JobTokensToAdd[];
    setJobTokensToAddFinished: Action<ITokensStoreModel, JobTokensToAdd[]>;

    createJobTokensToAdd: Action<ITokensStoreModel, JobTokensToAdd>;

    createJobTokensToAddFromFund: Action<
        ITokensStoreModel,
        {
            job: string;
            fund: FundWithDetailsEntity;
            addTokenFT?: boolean;
            addSubTokensFT?: boolean;
            swAddPrice?: boolean;
            swAddMetadata?: boolean;
            followUp?: boolean;
            keepAlive?: boolean;
        }
    >;

    createJobTokensToAddFromFunds: Action<
        ITokensStoreModel,
        {
            job: string;
            funds: FundWithDetailsEntity[];
            addTokenFT?: boolean;
            addSubTokensFT?: boolean;
            swAddPrice?: boolean;
            swAddMetadata?: boolean;
            followUp?: boolean;
            keepAlive?: boolean;
        }
    >;

    getFinishedJobTokensToAdd: Computed<ITokensStoreModel, (job: string) => Token_For_Store[] | undefined>;

    isExecuting: boolean;
    setIsExecuting: Action<ITokensStoreModel, boolean>;
    executeJobsTokensToAdd: Thunk<ITokensStoreModel>;

    tokensWithDetails: Token_For_Store[];
    setTokensWithDetails_: Action<ITokensStoreModel, Token_For_Store[]>;
    setTokensWithDetails: Thunk<ITokensStoreModel, Token_For_Store[]>;

    tokensWithDetailsAndValidity: Token_For_Store_With_Validity[];
    setTokensWithDetailsAndValidity: Action<ITokensStoreModel, Token_For_Store_With_Validity[]>;

    isAddingTokens: boolean;
    setIsAddingTokens: Action<ITokensStoreModel, boolean>;

    intervalIdForUpdateLoop: NodeJS.Timeout | undefined;
    setIntervalIdForUpdateLoop: Action<ITokensStoreModel, NodeJS.Timeout | undefined>;

    doUpdatePricesAutomatically: boolean;
    setDoUpdatePricesAutomatically: Action<ITokensStoreModel, boolean>;

    beginUpdateLoop: Thunk<ITokensStoreModel, { doUpdatePricesAutomatically?: boolean } | undefined, any, any, Promise<NodeJS.Timeout | undefined>>;
    stopUpdateLoop: Thunk<ITokensStoreModel, { cancelFollowUp?: boolean } | undefined>;

    cleanStore: Thunk<ITokensStoreModel>;

    addToken: Thunk<
        ITokensStoreModel,
        { token: Partial<Token_For_Store>; swAddPrice?: boolean; swAddMetadata?: boolean; swCreateMetadataWhenNotFound?: boolean; followUp?: boolean; keepAlive?: boolean },
        Promise<Token_For_Store>
    >;

    addTokens: Thunk<
        ITokensStoreModel,
        { tokens: Partial<Token_For_Store>[]; swAddPrice?: boolean; swAddMetadata?: boolean; swCreateMetadataWhenNotFound?: boolean; followUp?: boolean; keepAlive?: boolean },
        Promise<Token_For_Store[]>
    >;

    removeToken: Thunk<ITokensStoreModel, { CS: CS; TN_Hex: TN }>;

    // refreshPrices: Thunk<ITokensStoreModel, { forceRefreshFT?: boolean; forceRefreshSubTokensFT?: boolean; forceUseOracle?: boolean } | undefined>;
    refreshPrices: Thunk<ITokensStoreModel, { forceRefresh?: boolean; forceUseOracle?: boolean } | undefined>;

    isTokenPriceValid: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN) => boolean>;
    getTokenValidity: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN) => bigint | undefined>;
    getTokenPriceAndMetadata: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN) => Token_For_Store | undefined>;
    getTokensPriceAndMetadata: Computed<ITokensStoreModel, (tokens: Partial<Token_For_Store>[]) => Token_For_Store[]>;
    getTokenDecimals: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN) => Decimals | undefined>;

    showTokenPriceAndValidity: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN, swRounded?: boolean, showDecimals?: Decimals) => string>;
    showTokenPrice: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN, swRounded?: boolean, showDecimals?: Decimals) => string>;
    showTokenValidity: Computed<ITokensStoreModel, (CS: CS, TN_Hex: TN, swAddValidFor?: boolean) => string>;

    showTokenWithAmount: Computed<
        ITokensStoreModel,
        (amount: bigint | number | undefined, CS: CS, TN_Hex: TN, swRounded?: boolean, showDecimals?: Decimals, adaUsdPrice?: number) => string
    >;

    showTokenPriceMultipliedByAmount: Computed<
        ITokensStoreModel,
        (amount: bigint | number | undefined, CS: CS, TN_Hex: TN, swRounded?: boolean, showDecimals?: Decimals, decimalsInAmount?: Decimals) => string
    >;

    adaUsdPrice?: number;
    adaUsdPriceLastUpdated?: number;
    adaPriceError?: string;
    isFetchingAdaPrice: boolean;

    setAdaUsdPrice: Action<ITokensStoreModel, number | undefined>;
    setAdaUsdPriceLastUpdated: Action<ITokensStoreModel, number | undefined>;
    setAdaPriceError: Action<ITokensStoreModel, string | undefined>;
    setIsFetchingAdaPrice: Action<ITokensStoreModel, boolean>;

    fetchAdaUsdPrice: Thunk<ITokensStoreModel, void, any, any, Promise<number | undefined>>;

    adaUsdPriceHistoric: { price: number; timestamp: BigInt }[];
    adaPriceHistoricError?: string;
    isFetchingAdaPriceHistoric: boolean;

    setAdaUsdPriceHistoric: Action<ITokensStoreModel, { price: number; timestamp: BigInt }[]>;
    setAdaPriceHistoricError: Action<ITokensStoreModel, string | undefined>;
    setIsFetchingAdaPriceHistoric: Action<ITokensStoreModel, boolean>;

    fetchAdaUsdPriceHistoric: Thunk<ITokensStoreModel, void, any, any, Promise<{ price: number; timestamp: BigInt }[]>>;

    displayCurrency: DisplayCurrency;
    setDisplayCurrency: Action<ITokensStoreModel, DisplayCurrency>;
    toggleDisplayCurrency: Action<ITokensStoreModel>;
}

export const TokenStoreModel: ITokensStoreModel = {
    //----------------------------
    // #region Tokens
    //----------------------------
    serverTime: undefined,
    setServerTime: action((state, payload) => {
        state.serverTime = payload;
    }),
    //----------------------------
    serverTimeLastFetch: undefined,
    setServerTimeLastFetch: action((state, payload) => {
        state.serverTimeLastFetch = payload;
    }),
    //----------------------------
    serverTimeDiffWithBrowser: undefined,
    setServerTimeDiffWithBrowser: action((state, payload) => {
        state.serverTimeDiffWithBrowser = payload;
    }),
    //----------------------------
    isServerTimeLoaded: false,
    setIsServerTimeLoaded: action((state, payload) => {
        state.isServerTimeLoaded = payload;
    }),
    //----------------------------
    getServerTime: thunk(async (actions, payload, helpers): Promise<number | undefined> => {
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        let serverTime: number | undefined = undefined;
        let refreshServerTime = payload?.refresh ?? false;
        //----------------
        if (refreshServerTime === false && state.serverTime !== undefined && state.serverTimeLastFetch !== undefined) {
            // si pasaron mas de 10 minutos refresca siempre
            // si no, no refresca
            //--------------------------------------
            const now = Date.now();
            //--------------------------------------
            const diff = now - state.serverTimeLastFetch;
            refreshServerTime = diff > SYNC_SERVER_TIME_ALWAYS_MS;
            //--------------------------------------
        }
        //----------------
        if (state.serverTime === undefined || state.serverTimeDiffWithBrowser === undefined || state.serverTimeLastFetch === undefined || refreshServerTime === true) {
            //--------------------------------------
            console.log(`[TokensStore] - getServerTime - get real from server`);
            //----------------------------
            serverTime = await TimeApi.getServerTimeApi();
            //--------------------------------------
            const now = Date.now();
            //--------------------------------------
            actions.setServerTime(serverTime);
            //console.log(`ServerTime: ${serverTime} - JUST FECTH FROM SERVER - ${convertMillisToTime(serverTime!)}`);
            actions.setServerTimeLastFetch(now);
            actions.setServerTimeDiffWithBrowser(serverTime - now);
            actions.setIsServerTimeLoaded(true);
            //--------------------------------------
        } else {
            //--------------------------------------
            const now = Date.now();
            serverTime = now + state.serverTimeDiffWithBrowser;
            actions.setServerTime(serverTime);
            //--------------------------------------
        }
        return serverTime;
    }),
    //---------------------------
    jobTokensToAdd: [],
    //---------------------------
    setJobTokensToAdd: action((state, payload) => {
        state.jobTokensToAdd = payload;
    }),
    //---------------------------
    jobTokensToAddFinished: [],
    //---------------------------
    setJobTokensToAddFinished: action((state, payload) => {
        state.jobTokensToAddFinished = payload;
    }),
    //---------------------------
    createJobTokensToAdd: action((state, payload) => {
        //----------------------------
        const { job, tokens, followUp, keepAlive, swAddPrice, swAddMetadata, swCreateMetadataWhenNotFound } = payload;
        //----------------------------
        if (state.jobTokensToAdd.some((job) => job.job === payload.job)) {
            console.log(`[TokensStore] - Job: ${job} - createJobTokensToAdd - error job already exists`);
            return;
        }
        //----------------------------
        console.log(
            `[TokensStore] - Job: ${job} - createJobTokensToAdd - Tokens: ${tokens
                ?.map((t) => hexToStr(t.TN_Hex))
                .join(
                    ', '
                )} - followUp: ${followUp} - swAddPrice: ${swAddPrice} - swAddMetadata: ${swAddMetadata} - swCreateMetadataWhenNotFound: ${swCreateMetadataWhenNotFound} - keepAlive: ${keepAlive}`
        );
        //----------------------------
        state.jobTokensToAdd.push(payload);
    }),
    //----------------------------
    createJobTokensToAddFromFund: action((state, payload) => {
        //----------------------------
        const { fund, job } = payload;
        //----------------------------
        if (state.jobTokensToAdd.some((job) => job.job === payload.job)) {
            console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - error job already exists`);
            return;
        }
        //----------------------------
        const addTokenFT = payload.addTokenFT ?? true;
        const addSubTokensFT = payload.addSubTokensFT ?? true;
        const followUp = payload.followUp;
        const keepAlive = payload.keepAlive;
        const swAddPrice = payload.swAddPrice ?? true;
        const swAddMetadata = payload.swAddMetadata ?? true;
        //----------------------------
        if (fund === undefined) {
            console.error(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - error fund undefined`);
            return;
        }
        //----------------------------
        console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - fund: ${fund.name}`);
        //----------------------------
        if (fund._isDeployed === false) {
            console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - fund not deployed - not adding tokens`);
            return;
        }
        //----------------------------
        const validInvestUnit =
            (fund.investUnit?.iudInvestUnit?.iuValues !== undefined && fund.investUnit.iudInvestUnit.iuValues.length > 0) ||
            (fund.investUnit?.investUnitWithADAPrices?.iuValuesWithDetailsAndAmount !== undefined &&
                fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.length > 0);
        //----------------------------
        if (!validInvestUnit) {
            //LOAD INVEST UNIT
            console.error(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - error fund dont have invest unit`);
            return;
        }
        //----------------------------
        let tokensInFT: Partial<Token_For_Store>[] = [];
        //----------------------------
        if (
            fund.investUnit?.investUnitWithADAPrices?.iuValuesWithDetailsAndAmount !== undefined &&
            fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.length > 0
        ) {
            tokensInFT = fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.map((token) => {
                return { ...token, CS: token.CS, TN_Hex: token.TN_Hex, followUp, keepAlive };
            });
        } else if (fund.investUnit?.iudInvestUnit?.iuValues !== undefined && fund.investUnit.iudInvestUnit.iuValues.length > 0) {
            tokensInFT = fund.investUnit.iudInvestUnit.iuValues.map((iuValue) => {
                return { CS: iuValue[0], TN_Hex: iuValue[1], followUp, keepAlive };
            });
        }
        //----------------------------
        const tokenFTDetails = fund.tokenFT;
        //----------------------------
        const tokenFT: Partial<Token_For_Store> = {
            ...tokenFTDetails,
            CS: fund.getNET_id_CS(),
            TN_Hex: fund.fdFundFT_TN_Hex,
            isFT: true,
            tokensInFT: tokensInFT === undefined ? [] : (tokensInFT as unknown as Token_For_Store[]),
            followUp,
            keepAlive,
        };
        //-----------------------
        let tokensAll: Partial<Token_For_Store>[] = [];
        //-----------------------
        if (addSubTokensFT === true) {
            tokensAll = tokensAll.concat([...tokensInFT]);
        }
        //-----------------------
        if (addTokenFT === true) {
            tokensAll.push(tokenFT);
        }
        //-----------------------
        console.log(
            `[TokensStore] - Job: ${job} - createJobTokensToAddFromFund - Tokens: ${tokensAll
                ?.map((t) => hexToStr(t.TN_Hex))
                .join(', ')} - followUp: ${followUp} - swAddPrice: ${swAddPrice} - swAddMetadata: ${swAddMetadata} - keepAlive: ${keepAlive}`
        );
        //-----------------------
        state.jobTokensToAdd.push({ tokens: tokensAll, followUp, swAddPrice, swAddMetadata, keepAlive, job });
        //-----------------------
    }),
    //----------------------------
    createJobTokensToAddFromFunds: action((state, payload) => {
        //----------------------------
        const { funds, job } = payload;
        //----------------------------
        if (state.jobTokensToAdd.some((job) => job.job === payload.job)) {
            console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFunds - error job already exists`);
            return;
        }
        //----------------------------
        const addTokenFT = payload.addTokenFT ?? true;
        const addSubTokensFT = payload.addSubTokensFT ?? true;
        const followUp = payload.followUp;
        const swAddPrice = payload.swAddPrice ?? true;
        const swAddMetadata = payload.swAddMetadata ?? true;
        const keepAlive = payload.keepAlive;
        //----------------------------
        console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFunds - funds: ${funds.map((fund) => fund.name).join(', ')}`);
        //----------------------------
        let tokensInAllFT: Partial<Token_For_Store>[] = [];
        let tokensAllFT: Partial<Token_For_Store>[] = [];
        //----------------------------
        for (const fund of funds) {
            //----------------------------
            if (fund._isDeployed === false) {
                console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFunds - fund: ${fund.name} - not deployed - not adding tokens`);
                continue;
            }
            //----------------------------
            const validInvestUnit =
                (fund.investUnit?.iudInvestUnit?.iuValues !== undefined && fund.investUnit.iudInvestUnit.iuValues.length > 0) ||
                (fund.investUnit?.investUnitWithADAPrices?.iuValuesWithDetailsAndAmount !== undefined &&
                    fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.length > 0);
            //----------------------------
            if (!validInvestUnit) {
                //LOAD INVEST UNIT
                console.log(`[TokensStore] - Job: ${job} - createJobTokensToAddFromFunds - error fund dont have invest unit`);
                continue;
            }
            //----------------------------
            let tokensInFT: Partial<Token_For_Store>[] = [];
            //----------------------------
            if (
                fund.investUnit?.investUnitWithADAPrices?.iuValuesWithDetailsAndAmount !== undefined &&
                fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.length > 0
            ) {
                tokensInFT = fund.investUnit.investUnitWithADAPrices.iuValuesWithDetailsAndAmount.map((token) => {
                    return { ...token, CS: token.CS, TN_Hex: token.TN_Hex, followUp, keepAlive };
                });
            } else if (fund.investUnit?.iudInvestUnit?.iuValues !== undefined && fund.investUnit.iudInvestUnit.iuValues.length > 0) {
                tokensInFT = fund.investUnit!.iudInvestUnit.iuValues.map((iuValue) => {
                    return { CS: iuValue[0], TN_Hex: iuValue[1], followUp, keepAlive };
                });
            }
            //----------------------------
            tokensInAllFT = tokensInAllFT.concat(tokensInFT);
            //----------------------------
            const tokenFTDetails = fund.tokenFT;
            //----------------------------
            const tokenFT: Partial<Token_For_Store> = {
                ...tokenFTDetails,
                CS: fund.getNET_id_CS(),
                TN_Hex: fund.fdFundFT_TN_Hex,
                isFT: true,
                tokensInFT: tokensInFT === undefined ? [] : (tokensInFT as unknown as Token_For_Store[]),
                followUp,
                keepAlive,
            };
            //----------------------------
            tokensAllFT.push(tokenFT);
            //----------------------------
        }
        //-----------------------
        let tokensAll: Partial<Token_For_Store>[] = [];
        //-----------------------
        if (addSubTokensFT === true) {
            tokensAll = tokensAll.concat([...tokensInAllFT]);
        }
        //-----------------------
        if (addTokenFT === true) {
            tokensAll = tokensAll.concat([...tokensAllFT]);
        }
        //-----------------------
        console.log(
            `[TokensStore] - Job: ${job} - createJobTokensToAddFromFunds - Tokens: ${tokensAll
                ?.map((t) => hexToStr(t.TN_Hex))
                .join(', ')} - followUp: ${followUp} - swAddPrice: ${swAddPrice} - swAddMetadata: ${swAddMetadata} - keepAlive: ${keepAlive}`
        );
        //-----------------------
        state.jobTokensToAdd.push({ tokens: tokensAll, followUp, swAddPrice, swAddMetadata, keepAlive, job });
        //-----------------------
    }),
    //---------------------------
    getFinishedJobTokensToAdd: computed((state) => (job: string): Token_For_Store[] | undefined => {
        //----------------------------
        const tokensToAddJob = state.jobTokensToAddFinished.find((token) => token.job === job);
        //----------------------------
        if (tokensToAddJob === undefined) {
            return undefined;
        }
        //----------------------------
        // console.log(`[TokensStore] - Job: ${job} - getFinishedJobTokensToAdd - Init - len finished: ${state.jobTokensToAddFinished.length}`);
        //----------------------------
        const tokensToAddTokens = tokensToAddJob.tokens;
        const tokensWithMetadataAndPrice = tokensToAddTokens
            .map((token) => {
                return state.tokensWithDetails.find((token_) => token_.CS === token.CS && token_.TN_Hex === token.TN_Hex) || undefined;
            })
            .filter((token) => token !== undefined); // Filter out any null values (tokens not found)
        //----------------------------
        state.jobTokensToAddFinished = [...state.jobTokensToAddFinished].filter((tokenJob) => tokenJob.job !== job);
        //----------------------------
        // console.log(`[TokensStore] - Job: ${job} - getFinishedJobTokensToAdd - OK - len finished: ${state.jobTokensToAddFinished.length} - len tokens: ${tokensWithMetadataAndPrice.length}`);
        //-----------------------
        console.log(`[TokensStore] - Job: ${job} - getFinishedJobTokensToAdd - OK - Tokens: ${tokensWithMetadataAndPrice?.map((t) => hexToStr(t?.TN_Hex)).join(', ')}`);
        //-----------------------
        return tokensWithMetadataAndPrice as Token_For_Store[];
    }),
    //----------------------------
    isExecuting: false,
    setIsExecuting: action((state, payload) => {
        state.isExecuting = payload;
    }),
    //----------------------------
    executeJobsTokensToAdd: thunk(async (actions, payload, helpers) => {
        //----------------------------
        // console.log(`[TokensStore] - executeJobsTokensToAdd - Init`);
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        const jobTokensToAdd = [...state.jobTokensToAdd];
        //----------------------------
        actions.setIsExecuting(true);
        actions.setJobTokensToAdd([]);
        //----------------------------
        // let count = 0;
        // for (const tokenToAdd of jobTokensToAdd) {
        //     console.log(`[TokensStore] - executing adding tokens - Job: ${tokenToAdd.job} - ${count++ + 1} of ${state.jobTokensToAdd.length}`);
        //     await actions.addTokens({
        //         tokens: tokenToAdd.tokens,
        //         swAddPrice: tokenToAdd.swAddPrice,
        //         swAddMetadata: tokenToAdd.swAddMetadata,
        //         followUp: tokenToAdd.followUp,
        //         keepAlive: tokenToAdd.keepAlive,
        //     });
        // }
        //----------------------------
        const groupedJobs = jobTokensToAdd.reduce((acc: Record<string, JobTokensToAdd>, tokenToAdd) => {
            //----------------------------
            const key = `${tokenToAdd.swAddPrice}-${tokenToAdd.swAddMetadata}-${tokenToAdd.swCreateMetadataWhenNotFound}-${tokenToAdd.followUp}-${tokenToAdd.keepAlive}`;
            //----------------------------
            if (!acc[key]) {
                acc[key] = {
                    tokens: [],
                    swAddPrice: tokenToAdd.swAddPrice,
                    swAddMetadata: tokenToAdd.swAddMetadata,
                    swCreateMetadataWhenNotFound: tokenToAdd.swCreateMetadataWhenNotFound,
                    followUp: tokenToAdd.followUp,
                    keepAlive: tokenToAdd.keepAlive,
                    job: '',
                } as JobTokensToAdd;
            }
            //----------------------------
            acc[key].tokens.push(...tokenToAdd.tokens);
            acc[key].job = acc[key].job + (acc[key].job.length > 0 ? ', ' : '') + tokenToAdd.job;
            //----------------------------
            return acc;
        }, {});
        //----------------------------
        let count = 0;
        //----------------------------
        const keys = Object.keys(groupedJobs);
        //----------------------------
        for (const key of keys) {
            //----------------------------
            console.log(`[TokensStore] - executing adding grouped tokens - Group: ${count + 1} of ${keys.length} - Jobs: ${groupedJobs[key].job}`);
            //----------------------------
            await actions.addTokens({
                tokens: groupedJobs[key].tokens,
                swAddPrice: groupedJobs[key].swAddPrice,
                swAddMetadata: groupedJobs[key].swAddMetadata,
                swCreateMetadataWhenNotFound: groupedJobs[key].swCreateMetadataWhenNotFound,
                followUp: groupedJobs[key].followUp,
                keepAlive: groupedJobs[key].keepAlive,
            });
            //----------------------------
            count++;
        }

        //----------------------------
        // state = helpers.getState();
        //----------------------------
        actions.setJobTokensToAddFinished([...state.jobTokensToAddFinished, ...jobTokensToAdd]);
        //----------------------------
        actions.setIsExecuting(false);
        //----------------------------
        // console.log(`[TokensStore] - executeJobsTokensToAdd - All Jobs done`);
        //----------------------------
        return true;
    }),
    //----------------------------
    tokensWithDetails: [],
    setTokensWithDetails_: action((state, payload) => {
        state.tokensWithDetails = payload;
    }),
    //----------------------------
    setTokensWithDetails: thunk(async (actions, payload, helpers) => {
        //----------------------------
        console.log(`[TokensStore] - setTokensWithDetails - ${payload.map((token) => `${hexToStr(token.TN_Hex)}`).join(', ')}`);
        //----------------------------
        const tokensWithDetails = payload;
        actions.setTokensWithDetails_(tokensWithDetails);
        //----------------
        let serverTime = await actions.getServerTime();
        //----------------
        if (serverTime === undefined) {
            throw `serverTime is undefined`;
        }
        //----------------
        const newTokensWithDetailsAndValidity: Token_For_Store_With_Validity[] = [];
        //----------------
        tokensWithDetails
            .filter((token) => token.followUp === true)
            .map((token) => {
                if (token !== undefined && token.date !== undefined) {
                    const currentTimeMs = serverTime!;
                    const priceTimeMs = Number(token.date.toString());
                    const expiryTimeMs = priceTimeMs + MAX_PRICE_AGE_FOR_USE_MS;
                    const validTimeSecs = BigInt(Math.floor((expiryTimeMs - currentTimeMs) / 1000));
                    const newTokenWithDetailsAndValidity: Token_For_Store_With_Validity = { ...token, validity: validTimeSecs };
                    newTokensWithDetailsAndValidity.push(newTokenWithDetailsAndValidity);
                }
            });
        //----------------
        actions.setTokensWithDetailsAndValidity(newTokensWithDetailsAndValidity);
        //----------------
        // if (doLoop) {
        //     await actions.beginUpdateLoop({ doUpdatePricesAutomatically: state.doUpdatePricesAutomatically });
        // }
        //----------------
    }),
    //----------------------------
    tokensWithDetailsAndValidity: [],
    setTokensWithDetailsAndValidity: action((state, payload) => {
        state.tokensWithDetailsAndValidity = payload;
    }),
    //----------------------------
    isAddingTokens: false,
    setIsAddingTokens: action((state, payload) => {
        state.isAddingTokens = payload;
    }),
    //----------------------------
    intervalIdForUpdateLoop: undefined,
    setIntervalIdForUpdateLoop: action((state, payload) => {
        state.intervalIdForUpdateLoop = payload;
    }),
    //----------------------------
    doUpdatePricesAutomatically: true,
    setDoUpdatePricesAutomatically: action((state, payload) => {
        state.doUpdatePricesAutomatically = payload;
    }),
    //----------------------------
    beginUpdateLoop: thunk(async (actions, payload, helpers): Promise<NodeJS.Timeout | undefined> => {
        //----------------------------
        console.log(`[TokensStore] - beginUpdateLoop`);
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        if (state.intervalIdForUpdateLoop !== undefined) {
            clearInterval(state.intervalIdForUpdateLoop);
            actions.setIntervalIdForUpdateLoop(undefined);
        }
        //----------------------------
        let intervalIdForUpdateLoop: NodeJS.Timeout | undefined = undefined;
        //----------------
        actions.setDoUpdatePricesAutomatically(payload?.doUpdatePricesAutomatically ?? true);
        //----------------
        const updateLoop = async () => {
            //----------------------------
            // sconsole.log(`[TokensStore] - updateLoop`);
            //----------------------------
            let state = helpers.getState();
            //----------------------------
            const newTokensWithDetailsAndValidity: Token_For_Store_With_Validity[] = [];
            const newInvalidTokensWithDetailsAndValidity: Token_For_Store_With_Validity[] = [];
            //----------------------------
            let serverTime = await actions.getServerTime();
            // console.log(`[TokensStore] - updateLoop - serverTime: ${serverTime}`);
            //----------------
            if (serverTime === undefined) {
                throw `serverTime is undefined`;
            }
            //----------------
            for (const token_ of state.tokensWithDetails) {
                //----------------------------
                if (token_ !== undefined && token_.date !== undefined && token_.followUp === true) {
                    //----------------------------
                    // serverTime = await actions.getServerTime();
                    //----------------
                    //console.log(`ServerTime: ${serverTime} - ${convertMillisToTime(serverTime!)}`);
                    //----------------
                    const currentTimeMs = serverTime!;
                    const priceTimeMs = Number(token_.date.toString());
                    const expiryTimeMs = priceTimeMs + MAX_PRICE_AGE_FOR_USE_MS;
                    //----------------
                    const validTimeSecs = BigInt(Math.floor((expiryTimeMs - currentTimeMs) / 1000));
                    //----------------
                    const newTokenWithDetailsAndValidity: Token_For_Store_With_Validity = { ...token_, validity: validTimeSecs };
                    //----------------
                    newTokensWithDetailsAndValidity.push(newTokenWithDetailsAndValidity);
                    //----------------
                    if (validTimeSecs < 0 && state.doUpdatePricesAutomatically) {
                        newInvalidTokensWithDetailsAndValidity.push(newTokenWithDetailsAndValidity);
                    }
                    //----------------
                }
            }
            //----------------
            actions.setTokensWithDetailsAndValidity(newTokensWithDetailsAndValidity);
            //----------------
            if (state.doUpdatePricesAutomatically === true && newInvalidTokensWithDetailsAndValidity.length > 0) {
                //----------------
                console.log(`[TokensStore] - isLoading Previus: - ${state.isAddingTokens}`);
                // console.log ('setDoUpdatePricesAutomatically2');
                // alert('setDoUpdatePricesAutomatically2');
                //----------------
                actions.setIsAddingTokens(true);
                //----------------
                const newTokensWithDetails: Token_For_Store[] = state.tokensWithDetails;
                //----------------
                let tokensToUpdateWithNewPrices: Token_With_Price_And_Date_And_Signature[] = [];
                //----------------
                try {
                    tokensToUpdateWithNewPrices = await PriceApi.get_Tokens_PriceADAx1e6_Api(
                        newInvalidTokensWithDetailsAndValidity.map((token) => ({ CS: token.CS, TN_Hex: token.TN_Hex })),
                        true
                    );
                } catch (error) {
                    console.error(`[TokensStore] - Error getting tokens prices: ${error}`);
                }
                //----------------
                newInvalidTokensWithDetailsAndValidity.map((token) => {
                    const tokenToUpdateWithNewPrice = tokensToUpdateWithNewPrices.find((token_) => token_.CS === token.CS && token_.TN_Hex === token.TN_Hex);
                    if (tokenToUpdateWithNewPrice !== undefined) {
                        token.priceADAx1e6 = tokenToUpdateWithNewPrice?.priceADAx1e6;
                        token.date = tokenToUpdateWithNewPrice?.date;
                        token.signature = tokenToUpdateWithNewPrice?.signature;
                        //----------------------------
                        const index = newTokensWithDetails.findIndex((token_) => token.CS === token_.CS && token.TN_Hex === token_.TN_Hex);
                        newTokensWithDetails[index] = token;
                        //----------------------------
                    }
                });
                //----------------------------
                actions.setTokensWithDetails(newTokensWithDetails);
                //----------------------------
                actions.setIsAddingTokens(false);
                //----------------------------
            }
        };
        //----------------
        await updateLoop(); // Call it once immediately
        //----------------
        intervalIdForUpdateLoop = setInterval(updateLoop, 1000); // Then set up the interval
        //----------------
        actions.setIntervalIdForUpdateLoop(intervalIdForUpdateLoop);
        //----------------
        // Handle the cleanup (interval clearing) when the component unmounts or tokensWithDetails changes
        // When you call this thunk inside a useEffect in your component,
        // it ensures that the interval gets cleared when the component unmounts or when the dependencies change.
        // useEffect(() => {
        //     return () => {
        //         tokensStore.stopUpdateLoop();
        //     };
        // }, []);
        return intervalIdForUpdateLoop;
    }),
    //----------------------------
    stopUpdateLoop: thunk(async (actions, payload, helpers) => {
        //----------------------------
        const cancelFollowUp = payload?.cancelFollowUp ?? false;
        //----------------------------
        console.log(`[TokensStore] - stopUpdateLoop`);
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        if (state.intervalIdForUpdateLoop !== undefined) {
            clearInterval(state.intervalIdForUpdateLoop);
            actions.setIntervalIdForUpdateLoop(undefined);
        }
        //-----------------------
        if (cancelFollowUp === true) {
            //-----------------------
            //set followUp to false for all tokens
            const newTokensWithDetails = [...state.tokensWithDetails.map((token) => ({ ...token, followUp: false }))]; // Create a copy
            //-----------------------
            await actions.setTokensWithDetails(newTokensWithDetails);
            //----------------------------
        }
    }),
    //----------------------------
    cleanStore: thunk(async (actions, payload, helpers) => {
        //----------------------------
        console.log(`[TokensStore] - cleanStore`);
        //----------------------------
        await actions.stopUpdateLoop();
        //----------------------------
        actions.setJobTokensToAdd([]);
        actions.setJobTokensToAddFinished([]);
        await actions.setTokensWithDetails([]);
        //----------------------------
    }),
    //----------------------------
    addToken: thunk(async (actions, payload, helpers) => {
        //----------------------------
        const { token: newToken } = payload;
        const swAddPrice = payload.swAddPrice ?? true;
        const swAddMetadata = payload.swAddMetadata ?? true;
        const swCreateMetadataWhenNotFound = payload.swCreateMetadataWhenNotFound ?? true;
        const followUp = payload.followUp;
        const keepAlive = payload.keepAlive;
        //----------------------------
        const results: Token_For_Store[] = await actions.addTokens({ tokens: [newToken], swAddPrice, swAddMetadata, swCreateMetadataWhenNotFound, followUp, keepAlive });
        //----------------------------
        if (results.length > 0) {
            return results[0];
        }
        //----------------------------
    }),
    //----------------------------
    addTokens: thunk(async (actions, payload, helpers) => {
        //----------------------------
        const { tokens: newTokens } = payload;
        const swAddPrice = payload.swAddPrice ?? true;
        const swAddMetadata = payload.swAddMetadata ?? true;
        const swCreateMetadataWhenNotFound = payload.swCreateMetadataWhenNotFound ?? true;
        const followUp = payload.followUp;
        const keepAlive = payload.keepAlive;
        //----------------------------
        console.log(
            `[TokensStore] - addTokens - Tokens: ${newTokens
                .map((token) => `${hexToStr(token.TN_Hex)}`)
                .join(
                    ', '
                )} - followUp: ${followUp} - swAddPrice: ${swAddPrice} - swAddMetadata: ${swAddMetadata} - swCreateMetadataWhenNotFound: ${swCreateMetadataWhenNotFound} -keepAlive: ${keepAlive}`
        );
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        const tokensAll: Partial<Token_For_Store>[] = newTokens;
        //-----------------------
        //get all the differents
        const uniqueTokensMap = new Map();
        tokensAll.forEach((token) => {
            const uniqueKey = `${token.CS}-${token.TN_Hex}`; // Combine CS and TN_Hex to create a unique key
            if (!uniqueTokensMap.has(uniqueKey)) {
                uniqueTokensMap.set(uniqueKey, token);
            }
        });
        //-----------------------
        const uniqueTokens = Array.from(uniqueTokensMap.values()) as Partial<Token_For_Store>[];
        //-----------------------
        if (
            uniqueTokens.some(
                (newToken) =>
                    (newToken.priceADAx1e6 === undefined && swAddPrice) ||
                    ((newToken.ticker === undefined || newToken.decimals === undefined || newToken.image === undefined || newToken.colorHex === undefined) && swAddMetadata)
            )
        ) {
            //    || newToken.metadata_raw === undefined
            actions.setIsAddingTokens(true);
        }
        //-----------------------
        let newTokensWithDetails = [...state.tokensWithDetails]; // Create a copy
        //-----------------------
        const tokensToUpdatePrice: Partial<Token_For_Store>[] = [];
        const tokensToUpdatePriceWithForceRefresh: Partial<Token_For_Store>[] = [];
        const tokensToUpdateMetadata: Partial<Token_For_Store>[] = [];
        //-----------------------
        for (const newToken of uniqueTokens) {
            //----------------------------
            if (!isToken_CS_And_TN_Valid(newToken.CS, newToken.TN_Hex)) {
                console.error(`[TokensStore] - addTokens - invalid CS or TN_Hex - ${newToken.CS} - ${newToken.TN_Hex}`);
                continue;
            }
            //-----------------------
            const priceIndex = newTokensWithDetails.findIndex((price) => price.CS === newToken.CS && price.TN_Hex === newToken.TN_Hex);
            //-----------------------
            if (priceIndex === -1) {
                // si no lo estoy siguiendo
                //-----------------------
                if ((newToken.priceADAx1e6 === undefined || newToken.date === undefined) && swAddPrice) {
                    //-----------------------
                    const finalFolloUp = newToken.followUp ?? followUp;
                    //-----------------------
                    if (finalFolloUp === true) {
                        tokensToUpdatePriceWithForceRefresh.push(newToken);
                    } else {
                        tokensToUpdatePrice.push(newToken);
                    }
                }
                //-----------------------
                if ((newToken.ticker === undefined || newToken.decimals === undefined || newToken.image === undefined || newToken.colorHex === undefined) && swAddMetadata) {
                    tokensToUpdateMetadata.push(newToken);
                }
            } else {
                //-----------------------
                // si lo estoy siguiendo,
                //-----------------------
                const oldToken = newTokensWithDetails[priceIndex];
                //-----------------------
                // primero reviso si el nuevo token tiene o no precio y si quiero tenerlo
                //-----------------------
                if ((newToken.priceADAx1e6 === undefined || newToken.date === undefined) && swAddPrice) {
                    //-----------------------
                    const finalFolloUp = newToken.followUp ?? followUp;
                    //-----------------------
                    if (finalFolloUp === true) {
                        tokensToUpdatePriceWithForceRefresh.push(newToken);
                    } else {
                        // si no tiene precio, quiero tenerlo, pero no es folloUp, puedo ver si el antiguo tiene, si no, lo busco
                        if (oldToken.priceADAx1e6 === undefined || oldToken.date === undefined) {
                            tokensToUpdatePrice.push(newToken);
                        }
                    }
                }
                //-----------------------
                // luego reviso si el nuevo token tiene o no metadata y si quiero tenerla
                if ((newToken.ticker === undefined || newToken.decimals === undefined || newToken.image === undefined || newToken.colorHex === undefined) && swAddMetadata) {
                    // si no tiene metadata, quiero tenerla, puedo ver si el antiguo tiene, si no, lo busco
                    if (oldToken.ticker === undefined || oldToken.decimals === undefined || oldToken.image === undefined || oldToken.colorHex === undefined) {
                        tokensToUpdateMetadata.push(newToken);
                    }
                }
            }
        }
        //-----------------------
        let tokensToUpdateWithNewPrices: Token_With_Price_And_Date_And_Signature[] = [];
        if (tokensToUpdatePrice.length > 0) {
            //-----------------------
            console.log(`[TokensStore] - addTokens - Tokens: ${tokensToUpdatePrice.map((token) => `${hexToStr(token.TN_Hex)}`).join(', ')} - Fetching prices...`);
            //-----------------------
            tokensToUpdateWithNewPrices = await PriceApi.get_Tokens_PriceADAx1e6_Api(
                tokensToUpdatePrice.map((token) => ({ CS: token.CS!, TN_Hex: token.TN_Hex! })),
                false,
                MAX_PRICE_AGE_FOR_APROXIMATED_USE_MS
            );
        }
        let tokensToUpdateWithForceRefreshWithNewPrices: Token_With_Price_And_Date_And_Signature[] = [];
        if (tokensToUpdatePriceWithForceRefresh.length > 0) {
            //-----------------------
            console.log(`[TokensStore] - addTokens - Tokens: ${tokensToUpdatePriceWithForceRefresh.map((token) => `${hexToStr(token.TN_Hex)}`).join(', ')} - Fetching prices...`);
            //-----------------------
            tokensToUpdateWithForceRefreshWithNewPrices = await PriceApi.get_Tokens_PriceADAx1e6_Api(
                tokensToUpdatePriceWithForceRefresh.map((token) => ({ CS: token.CS!, TN_Hex: token.TN_Hex! })),
                true
            );
        }
        let tokensToUpdateMetadataWithNewMetadata: TokenMetadataEntity[] = [];
        if (tokensToUpdateMetadata.length > 0) {
            //-----------------------
            console.log(`[TokensStore] - addTokens - Tokens: ${tokensToUpdateMetadata.map((token) => `${hexToStr(token.TN_Hex)}`).join(', ')} - Fetching metadatas...`);
            //-----------------------
            tokensToUpdateMetadataWithNewMetadata = await TokenMetadataApi.get_Tokens_MetadataApi(
                tokensToUpdateMetadata.map((token) => ({ CS: token.CS!, TN_Hex: token.TN_Hex! })),
                undefined,
                swCreateMetadataWhenNotFound,
                TokenMetadataEntity.optionsGetForTokenStore
            );
        }
        //----------------
        for (const newToken of uniqueTokens) {
            //-----------------------
            let tokenToUpdateWithNewPrice = tokensToUpdateWithNewPrices.find((token_) => token_.CS === newToken.CS && token_.TN_Hex === newToken.TN_Hex);
            //-----------------------
            if (tokenToUpdateWithNewPrice === undefined) {
                tokenToUpdateWithNewPrice = tokensToUpdateWithForceRefreshWithNewPrices.find((token_) => token_.CS === newToken.CS && token_.TN_Hex === newToken.TN_Hex);
            }
            //-----------------------
            if (tokenToUpdateWithNewPrice !== undefined) {
                newToken.priceADAx1e6 = tokenToUpdateWithNewPrice.priceADAx1e6;
                newToken.date = tokenToUpdateWithNewPrice.date;
                newToken.signature = tokenToUpdateWithNewPrice.signature;
            }
            //-----------------------
            const tokenToUpdateMetadataWithNewMetadata = tokensToUpdateMetadataWithNewMetadata.find((token_) => token_.CS === newToken.CS && token_.TN_Hex === newToken.TN_Hex);
            //-----------------------
            if (tokenToUpdateMetadataWithNewMetadata !== undefined) {
                newToken.ticker = tokenToUpdateMetadataWithNewMetadata.ticker;
                newToken.decimals = tokenToUpdateMetadataWithNewMetadata.decimals;
                newToken.image = tokenToUpdateMetadataWithNewMetadata.image;
                newToken.colorHex = tokenToUpdateMetadataWithNewMetadata.colorHex;
                newToken.metadata_raw = tokenToUpdateMetadataWithNewMetadata.metadata_raw;
            }
            //-----------------------
            const priceIndex = newTokensWithDetails.findIndex((price) => price.CS === newToken.CS && price.TN_Hex === newToken.TN_Hex);
            //-----------------------
            if (priceIndex === -1) {
                //-----------------------
                const newTokenToAdd: Token_For_Store = {
                    ...newToken,
                    CS: newToken.CS!,
                    TN_Hex: newToken.TN_Hex!,
                    decimals: newToken.decimals,
                    priceADAx1e6: newToken.priceADAx1e6,
                    date: newToken.date,
                    signature: newToken.signature,
                    isFT: newToken.isFT ?? false,
                    tokensInFT: newToken.tokensInFT ?? [],
                    followUp: newToken.followUp ?? followUp,
                    keepAlive: newToken.keepAlive ?? keepAlive,
                };
                //-----------------------
                newTokensWithDetails.push(newTokenToAdd); // Update the copy
                //-----------------------
                // Separate items into two lists based on `keepAlive`
                const keepAliveItems = newTokensWithDetails.filter((item) => item.keepAlive === true);
                const nonKeepAliveItems = newTokensWithDetails.filter((item) => !(item.keepAlive === true));
                // If nonKeepAliveItems list is longer than 30, remove the first item
                if (nonKeepAliveItems.length > 30) {
                    nonKeepAliveItems.shift(); // Removes the first item of the nonKeepAliveItems list
                }
                // Concatenate the modified nonKeepAliveItems list back with the keepAliveItems list
                newTokensWithDetails = [...keepAliveItems, ...nonKeepAliveItems];
                //-----------------------
            } else {
                //-----------------------
                // si lo estoy siguiendo, veo si la fecha del actual es mayor a la del guardado o si en el guardado no tenia fecha
                // ademas controlo que tenga precio antes de suplantarlo
                //-----------------------
                const oldToken = newTokensWithDetails[priceIndex];
                //-----------------------
                let oldTokenUpdated: Token_For_Store = {
                    ...oldToken,
                };
                //-----------------------
                let swUpdate = false;
                //-----------------------
                const updatePriceADA = isTokenADA(oldTokenUpdated.CS, oldTokenUpdated.TN_Hex) && newToken.priceADAx1e6 !== undefined;
                const updatePriceOthers =
                    !isTokenADA(oldTokenUpdated.CS, oldTokenUpdated.TN_Hex) &&
                    newToken.date !== undefined &&
                    newToken.priceADAx1e6 !== undefined &&
                    ((oldToken.date !== undefined && newToken.date > oldToken.date) || oldToken.priceADAx1e6 === undefined || oldToken.date === undefined);
                //-----------------------
                if (updatePriceADA || updatePriceOthers) {
                    //-----------------------
                    // si existe voy a actualizarlo
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        priceADAx1e6: newToken.priceADAx1e6,
                        date: newToken.date,
                        signature: newToken.signature,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                if (newToken.ticker !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        ticker: newToken.ticker,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                if (newToken.decimals !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        decimals: newToken.decimals,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                if (newToken.image !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        image: newToken.image,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                if (newToken.colorHex !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        colorHex: newToken.colorHex,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                if (newToken.metadata_raw !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        metadata_raw: newToken.metadata_raw,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                const finalFolloUp = newToken.followUp ?? followUp;
                //-----------------------
                if (finalFolloUp !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        followUp: finalFolloUp,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                const finalkeepAlive = newToken.keepAlive ?? keepAlive;
                //-----------------------
                if (finalkeepAlive !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        keepAlive: finalkeepAlive,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                if (newToken.isFT !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        isFT: newToken.isFT,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                if (newToken.tokensInFT !== undefined) {
                    //-----------------------
                    oldTokenUpdated = {
                        ...oldTokenUpdated,
                        tokensInFT: newToken.tokensInFT,
                    };
                    //-----------------------
                    swUpdate = true;
                    //-----------------------
                }
                //-----------------------
                if (swUpdate === true) {
                    //-----------------------
                    newTokensWithDetails[priceIndex] = oldTokenUpdated; // Update the copy
                    //-----------------------
                }
            }
        }
        //-----------------------
        await actions.setTokensWithDetails(newTokensWithDetails);
        //-----------------------
        state = helpers.getState();
        //-----------------------
        const result = state.getTokensPriceAndMetadata(uniqueTokens as Token_For_Store[]);
        //-----------------------
        actions.setIsAddingTokens(false);
        //-----------------------
        console.log(`[TokensStore] - addTokens - OK - Tokens: ${uniqueTokens.map((token) => `${hexToStr(token.TN_Hex)}`).join(', ')}`);
        //-----------------------
        return result;
        //-----------------------
    }),
    //----------------------------
    removeToken: thunk(async (actions, token, helpers) => {
        //----------------------------
        console.log(`[TokensStore] - removeToken`);
        //----------------------------
        let state = helpers.getState();
        //----------------------------
        const newTokensWithDetails = [...state.tokensWithDetails].filter((token_) => !(token_.CS === token.CS && token_.TN_Hex === token.TN_Hex));
        //----------------------------
        await actions.setTokensWithDetails(newTokensWithDetails);
        //----------------------------
        if (newTokensWithDetails.length === 0 && state.intervalIdForUpdateLoop !== undefined) {
            await actions.stopUpdateLoop();
        }
        //----------------------------
    }),
    //----------------------------
    refreshPrices: thunk(async (actions, payload, helpers) => {
        //----------------------------
        //TODO: forceRefresh estaba defualt true, no se por que, lo puse false
        const forceRefresh = payload?.forceRefresh ?? false;
        const forceUseOracle = payload?.forceUseOracle ?? false;
        //----------------------------
        console.log(`[TokensStore] - refreshPrices - forceRefresh: ${forceRefresh} - forceUseOracle: ${forceUseOracle}`);
        //----------------------------
        let state = helpers.getState();
        //----------------
        actions.getServerTime({ refresh: true });
        //----------------
        const newTokensWithDetails: Token_For_Store[] = [];
        //----------------
        const tokensToUpdate = state.tokensWithDetails.filter((token) => token.followUp === true);
        const tokensNoUpdate = state.tokensWithDetails.filter((token) => token.followUp !== true);
        //----------------
        if (tokensToUpdate.length === 0) {
            console.error(`[TokensStore] - Nothing to update`);
            return;
        }
        //----------------
        actions.setIsAddingTokens(true);
        //----------------
        let tokensToUpdateWithNewPrices: Token_With_Price_And_Date_And_Signature[] = [];
        try {
            tokensToUpdateWithNewPrices = await PriceApi.get_Tokens_PriceADAx1e6_Api(
                tokensToUpdate.map((token) => ({ CS: token.CS, TN_Hex: token.TN_Hex })),
                forceRefresh,
                undefined,
                forceUseOracle
            );
        } catch (error) {
            console.error(`[TokensStore] - Error getting tokens prices: ${error}`);
        }
        //----------------
        tokensToUpdate.map((token) => {
            const tokenToUpdateWithNewPrice = tokensToUpdateWithNewPrices.find((token_) => token_.CS === token.CS && token_.TN_Hex === token.TN_Hex);
            if (tokenToUpdateWithNewPrice !== undefined) {
                token.priceADAx1e6 = tokenToUpdateWithNewPrice?.priceADAx1e6;
                token.date = tokenToUpdateWithNewPrice?.date;
                token.signature = tokenToUpdateWithNewPrice?.signature;
                newTokensWithDetails.push(token);
            } else {
                newTokensWithDetails.push(token);
            }
        });
        //----------------
        newTokensWithDetails.push(...tokensNoUpdate);
        //----------------
        actions.setIsAddingTokens(false);
        //----------------
        await actions.setTokensWithDetails(newTokensWithDetails);
        //----------------
    }),
    //----------------------------
    isTokenPriceValid: computed((state) => (CS: CS, TN_Hex: TN): boolean => {
        //-----------------------
        if (isTokenADA(CS, TN_Hex)) {
            return true;
        }
        //-----------------------
        const validity = state.getTokenValidity(CS, TN_Hex);
        //-----------------------
        if (validity !== undefined) {
            const secondsLeft = Number(validity);
            if (secondsLeft > 0) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }),
    //----------------------------
    getTokenValidity: computed((state) => (CS: CS, TN_Hex: TN): bigint | undefined => {
        //-----------------------
        if (isTokenADA(CS, TN_Hex)) {
            return BigInt(Number.MAX_SAFE_INTEGER);
        }
        //-----------------------
        const tokenIndex = state.tokensWithDetailsAndValidity.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
        if (tokenIndex !== -1) {
            return state.tokensWithDetailsAndValidity[tokenIndex].validity;
        } else {
            return undefined;
        }
    }),
    //----------------------------
    getTokenPriceAndMetadata: computed((state) => (CS: CS, TN_Hex: TN): Token_For_Store | undefined => {
        const tokenIndex = state.tokensWithDetails.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
        if (tokenIndex !== -1) {
            return state.tokensWithDetails[tokenIndex];
        }
    }),
    //----------------------------
    getTokensPriceAndMetadata: computed((state) => (tokens) => {
        //----------------------------
        // console.log('[TokensMetadataStore] - getTokens - actual length: ' + state.tokens.length);
        //----------------------------
        const res = tokens
            .map((token) => {
                return state.tokensWithDetails.find((token_) => token_.CS === token.CS && token_.TN_Hex === token.TN_Hex) || undefined;
            })
            .filter((token) => token !== undefined); // Filter out any null values (tokens not found)
        //----------------------------
        return res as Token_For_Store[];
    }),
    //--------------------------
    getTokenDecimals: computed((state) => (CS: CS, TN_Hex: TN): Decimals | undefined => {
        const tokenIndex = state.tokensWithDetails.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
        if (tokenIndex !== -1) {
            return state.tokensWithDetails[tokenIndex].decimals;
        }
    }),
    //----------------------------
    showTokenValidity: computed((state) => (CS: CS, TN_Hex: TN, swAddValidFor?: boolean): string => {
        //-----------------------
        if (isTokenADA(CS, TN_Hex)) {
            return `Valid`;
        }
        //-----------------------
        const validity = state.getTokenValidity(CS, TN_Hex);
        //-----------------------
        if (validity !== undefined) {
            //-----------------------
            if (state.isAddingTokens === true) {
                return 'Refreshing...';
            } else {
                const secondsLeft = Number(validity);
                if (secondsLeft > 0) {
                    return `${swAddValidFor === true ? `Valid for: ` : ``}${secondsLeft}s`;
                } else {
                    return `Refresh Price`;
                }
            }
        } else {
            return '...';
        }
    }),
    //----------------------------
    showTokenWithAmount: computed(
        (state) =>
            (amount: bigint | number | undefined, CS: CS, TN_Hex: TN, swRounded: boolean = false, showDecimals: Decimals = 2, adaUsdPrice?: number) => {
                //-----------------------
                if (isTokenADA(CS, TN_Hex)) {
                    adaUsdPrice = adaUsdPrice === undefined ? state.adaUsdPrice : adaUsdPrice;
                    if (state.displayCurrency === 'ada' || adaUsdPrice === undefined) {
                        return `${formatAmountWithUnit(amount, TOKEN_ADA_SYMBOL, swRounded ? showDecimals : TOKEN_ADA_DECIMALS, swRounded, TOKEN_ADA_DECIMALS)}`;
                    } else {
                        const amountUSD = amount === undefined ? amount : adaUsdPrice * Number(amount.toString());
                        return `${formatAmountWithUnit(
                            amountUSD,
                            TOKEN_USD_SYMBOL,
                            swRounded ? TOKEN_USD_DECIMALS : TOKEN_USD_DECIMALS,
                            false,
                            TOKEN_ADA_DECIMALS,
                            undefined,
                            'preFix'
                        )}`;
                    }
                }
                //-----------------------
                const tokenIndex = state.tokensWithDetails.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
                if (tokenIndex !== -1 && state.tokensWithDetails[tokenIndex].decimals !== undefined) {
                    return `${formatAmountWithUnit(
                        amount,
                        state.tokensWithDetails[tokenIndex].ticker,
                        swRounded ? showDecimals : state.tokensWithDetails[tokenIndex].decimals,
                        swRounded,
                        state.tokensWithDetails[tokenIndex].decimals
                    )}`;
                } else {
                    if (TN_Hex !== undefined) {
                        return `... ${hexToStr(TN_Hex)}`;
                    } else {
                        return `...`;
                    }
                }
            }
    ),
    //----------------------------
    showTokenPrice: computed((state) => (CS: CS, TN_Hex: TN, swRounded: boolean = false, showDecimals: Decimals = 2): string => {
        //-----------------------
        // console.log(`[TokensStore] - showTokenPrice - ${CS} - ${TN_Hex} - swRounded: ${swRounded} - showDecimals: ${showDecimals}`);
        //-----------------------
        if (isTokenADA(CS, TN_Hex)) {
            const priceADAx1e6 = 1000000n;
            const priceADAx1e6ofTokenBaseUnit = priceADAx1e6 * BigInt(10 ** TOKEN_ADA_DECIMALS);
            if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                return `${formatAmountWithUnit(
                    priceADAx1e6ofTokenBaseUnit,
                    TOKEN_ADA_SYMBOL,
                    swRounded ? showDecimals : TOKEN_ADA_DECIMALS,
                    swRounded,
                    (TOKEN_ADA_DECIMALS + PRICEx1e6_DECIMALS) as Decimals
                )}`;
            } else {
                const priceUSDx1e6ofTokenBaseUnit = state.adaUsdPrice * Number(priceADAx1e6ofTokenBaseUnit.toString());
                return `${formatAmountWithUnit(
                    priceUSDx1e6ofTokenBaseUnit,
                    TOKEN_USD_SYMBOL,
                    swRounded ? TOKEN_USD_DECIMALS : TOKEN_USD_DECIMALS,
                    false,
                    (TOKEN_ADA_DECIMALS + PRICEx1e6_DECIMALS) as Decimals,
                    undefined,
                    'preFix'
                )}`;
            }
        }
        //-----------------------
        const tokenIndex = state.tokensWithDetails.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
        if (tokenIndex !== -1) {
            //-----------------------
            const priceADAx1e6ofTokenBaseUnit =
                state.tokensWithDetails[tokenIndex].priceADAx1e6 !== undefined && state.tokensWithDetails[tokenIndex].decimals !== undefined
                    ? BigInt(state.tokensWithDetails[tokenIndex].priceADAx1e6!) * BigInt(10 ** state.tokensWithDetails[tokenIndex].decimals!)
                    : undefined;
            //-----------------------
            if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                const formatPrice = formatAmountWithUnit(
                    priceADAx1e6ofTokenBaseUnit,
                    TOKEN_ADA_SYMBOL,
                    swRounded ? showDecimals : TOKEN_ADA_DECIMALS,
                    swRounded,
                    (TOKEN_ADA_DECIMALS + PRICEx1e6_DECIMALS) as Decimals
                );
                return `${formatPrice}`;
            } else {
                const priceUSDx1e6ofTokenBaseUnit =
                    priceADAx1e6ofTokenBaseUnit === undefined ? priceADAx1e6ofTokenBaseUnit : state.adaUsdPrice * Number(priceADAx1e6ofTokenBaseUnit.toString());
                const formatPrice = formatAmountWithUnit(
                    priceUSDx1e6ofTokenBaseUnit,
                    TOKEN_USD_SYMBOL,
                    swRounded ? TOKEN_USD_DECIMALS : TOKEN_USD_DECIMALS,
                    false,
                    (TOKEN_ADA_DECIMALS + PRICEx1e6_DECIMALS) as Decimals,
                    undefined,
                    'preFix'
                );
                return `${formatPrice}`;
            }
            //-----------------------
        } else {
            if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                return `... ${TOKEN_ADA_SYMBOL}`;
            } else {
                return `... ${TOKEN_USD_SYMBOL}`;
            }
        }
    }),

    //----------------------------
    showTokenPriceAndValidity: computed((state) => (CS: CS, TN_Hex: TN, swRounded: boolean = false, showDecimals: Decimals = 2): string => {
        //-----------------------
        if (isTokenADA(CS, TN_Hex)) {
            //-----------------------
            const formatPrice = state.showTokenPrice(CS, TN_Hex, swRounded, showDecimals);
            //-----------------------
            return `${formatPrice} - Valid`;
        }
        //-----------------------
        const tokenIndex = state.tokensWithDetailsAndValidity.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
        if (tokenIndex !== -1) {
            //-----------------------
            const secondsLeft = state.tokensWithDetailsAndValidity[tokenIndex].validity !== undefined ? Number(state.tokensWithDetailsAndValidity[tokenIndex].validity) : 0;
            //-----------------------
            const formatPrice = state.showTokenPrice(CS, TN_Hex, swRounded, showDecimals);
            //-----------------------
            if (secondsLeft > 0) {
                return `${formatPrice} - Valid for ${secondsLeft} seconds`;
            } else {
                return `${formatPrice} - Not Valid - Refresh Price`;
            }
        } else {
            return '...';
        }
    }),
    //----------------------------
    showTokenPriceMultipliedByAmount: computed(
        (state) =>
            (amount: bigint | number | undefined, CS: CS, TN_Hex: TN, swRounded: boolean = false, showDecimals: Decimals = 2, decimalsInAmount: Decimals = 0) => {
                //-----------------------
                if (typeof amount !== 'number' && amount !== undefined) {
                    amount = Number(amount);
                }
                if (isTokenADA(CS, TN_Hex)) {
                    const priceADAx1e6 = 1000000n;
                    const priceADAx1e6ByAmount = amount !== undefined ? Number(priceADAx1e6) * amount : undefined;
                    if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                        return `${formatAmountWithUnit(
                            priceADAx1e6ByAmount,
                            TOKEN_ADA_SYMBOL,
                            swRounded ? showDecimals : ((TOKEN_ADA_DECIMALS + decimalsInAmount + PRICEx1e6_DECIMALS) as Decimals),
                            swRounded,
                            (TOKEN_ADA_DECIMALS + decimalsInAmount + PRICEx1e6_DECIMALS) as Decimals
                        )}`;
                    } else {
                        const priceUSDx1e6ByAmount = priceADAx1e6ByAmount === undefined ? priceADAx1e6ByAmount : state.adaUsdPrice * Number(priceADAx1e6ByAmount.toString());
                        return `${formatAmountWithUnit(
                            priceUSDx1e6ByAmount,
                            TOKEN_USD_SYMBOL,
                            swRounded ? TOKEN_USD_DECIMALS : TOKEN_USD_DECIMALS,
                            false,
                            (TOKEN_ADA_DECIMALS + decimalsInAmount + PRICEx1e6_DECIMALS) as Decimals,
                            undefined,
                            'preFix'
                        )}`;
                    }
                }
                //-----------------------
                const tokenIndex = state.tokensWithDetails.findIndex((token) => token.CS === CS && token.TN_Hex === TN_Hex);
                if (tokenIndex !== -1) {
                    const priceADAx1e6 =
                        state.tokensWithDetails[tokenIndex].priceADAx1e6 !== undefined && state.tokensWithDetails[tokenIndex].decimals !== undefined
                            ? BigInt(state.tokensWithDetails[tokenIndex].priceADAx1e6!)
                            : undefined;
                    const priceADAx1e6ByAmount = priceADAx1e6 !== undefined && amount !== undefined ? Number(priceADAx1e6) * amount : undefined;
                    //-----------------------
                    if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                        const formatPrice = formatAmountWithUnit(
                            priceADAx1e6ByAmount,
                            TOKEN_ADA_SYMBOL,
                            swRounded
                                ? showDecimals
                                : state.tokensWithDetails[tokenIndex].decimals === undefined
                                ? undefined
                                : ((TOKEN_ADA_DECIMALS + decimalsInAmount + state.tokensWithDetails[tokenIndex]!.decimals!) as Decimals),
                            swRounded,
                            (TOKEN_ADA_DECIMALS + decimalsInAmount + PRICEx1e6_DECIMALS) as Decimals
                        );
                        return `${formatPrice}`;
                    } else {
                        const priceUSDx1e6ByAmount = priceADAx1e6ByAmount === undefined ? priceADAx1e6ByAmount : state.adaUsdPrice * Number(priceADAx1e6ByAmount.toString());
                        const formatPrice = formatAmountWithUnit(
                            priceUSDx1e6ByAmount,
                            TOKEN_USD_SYMBOL,
                            swRounded ? TOKEN_USD_DECIMALS : TOKEN_USD_DECIMALS,
                            false,
                            (TOKEN_ADA_DECIMALS + decimalsInAmount + PRICEx1e6_DECIMALS) as Decimals,
                            undefined,
                            'preFix'
                        );
                        return `${formatPrice}`;
                    }
                } else {
                    if (state.displayCurrency === 'ada' || state.adaUsdPrice === undefined) {
                        return `... ${TOKEN_ADA_SYMBOL}`;
                    } else {
                        return `... ${TOKEN_USD_SYMBOL}`;
                    }
                }
            }
    ),
    //----------------------------
    // #endregion Tokens
    //----------------------------
    // #region Price
    //----------------------------

    adaUsdPrice: undefined,
    adaUsdPriceLastUpdated: undefined,
    adaPriceError: undefined,
    isFetchingAdaPrice: false,

    adaUsdPriceHistoric: [],
    adaPriceHistoricError: undefined,
    isFetchingAdaPriceHistoric: false,

    displayCurrency: 'ada',

    setAdaUsdPrice: action((state, payload) => {
        state.adaUsdPrice = payload;
    }),
    setAdaUsdPriceLastUpdated: action((state, payload) => {
        state.adaUsdPriceLastUpdated = payload;
    }),
    setAdaPriceError: action((state, payload) => {
        state.adaPriceError = payload;
    }),
    setIsFetchingAdaPrice: action((state, payload) => {
        state.isFetchingAdaPrice = payload;
    }),
    setDisplayCurrency: action((state, payload) => {
        state.displayCurrency = payload;
    }),
    toggleDisplayCurrency: action((state) => {
        state.displayCurrency = state.displayCurrency === 'ada' ? 'usd' : 'ada';
    }),

    fetchAdaUsdPrice: thunk(async (actions) => {
        console.log('[PriceStore] - fetchAdaUsdPrice - Init');
        actions.setIsFetchingAdaPrice(true);
        actions.setAdaPriceError(undefined);

        const getPriceFromProxy = async (): Promise<number | undefined> => {
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${ADA_PRICE_ENDPOINT}`, { method: 'GET' }, true);
            if (!response.ok) {
                throw new Error(`Proxy request failed with status: ${response.status} - ${response.statusText}`);
            }
            const data = (await response.json()) as AdaPriceApiResponse | undefined;
            const price = data?.price;
            if (typeof price !== 'number' || Number.isNaN(price) || price <= 0) {
                throw new Error('Invalid price returned by proxy endpoint');
            }
            return price;
        };

        try {
            const price = await getPriceFromProxy();
            actions.setAdaUsdPrice(price);
            actions.setAdaUsdPriceLastUpdated(Date.now());
            actions.setAdaPriceError(undefined); // Clear any previous errors since we got a valid price
            actions.setIsFetchingAdaPrice(false);
            console.log(`[PriceStore] - fetchAdaUsdPrice - OK - price: ${price}`);
            return price;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`[PriceStore] - fetchAdaUsdPrice - Error: ${message}`);
            actions.setIsFetchingAdaPrice(false);
            actions.setAdaPriceError(message);
            return undefined;
        }
    }),

    setAdaUsdPriceHistoric: action((state, payload) => {
        state.adaUsdPriceHistoric = payload;
    }),
    setAdaPriceHistoricError: action((state, payload) => {
        state.adaPriceHistoricError = payload;
    }),
    setIsFetchingAdaPriceHistoric: action((state, payload) => {
        state.isFetchingAdaPriceHistoric = payload;
    }),

    fetchAdaUsdPriceHistoric: thunk(async (actions) => {
        console.log('[PriceStore] - fetchAdaUsdPriceHistoric - Init');
        actions.setIsFetchingAdaPriceHistoric(true);
        actions.setAdaPriceHistoricError(undefined);

        const getPriceHistoricFromProxy = async (): Promise<{ price: number; timestamp: BigInt }[]> => {
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/${ADA_HISTORIC_PRICE_ENDPOINT}`, { method: 'GET' }, true);
            if (!response.ok) {
                throw new Error(`Proxy request failed with status: ${response.status} - ${response.statusText}`);
            }
            const data = (await response.json()) as AdaPriceHistoricApiResponse | undefined;
            if (data === undefined) {
                throw new Error('Invalid price returned by proxy endpoint');
            }
            const prices = data.prices.map((price) => {
                return {
                    price: price.price !== undefined ? Number(price.price) : 1,
                    timestamp: BigInt(price.date),
                };
            });
            return prices;
        };

        try {
            const prices = await getPriceHistoricFromProxy();
            actions.setAdaUsdPriceHistoric(prices);
            actions.setAdaPriceHistoricError(undefined);
            actions.setIsFetchingAdaPriceHistoric(false);
            console.log(`[PriceStore] - fetchAdaUsdPriceHistoric - OK - prices: ${toJson(prices)}`);
            return prices;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            console.error(`[PriceStore] - fetchAdaUsdPriceHistoric - Error: ${message}`);
            actions.setIsFetchingAdaPriceHistoric(false);
            actions.setAdaPriceHistoricError(message);
            return [];
        }
    }),

    //----------------------------
    // #endregion Price
    //----------------------------
};
